/**
 * 입력창 바로 위 안내 배너 — 실패·중단처럼 다음 행동이 필요한 것과, 무료 소진·게스트처럼 플랜·로그인을
 * 권하는 것. 입력창 위 8 에 떠 있는 둥근 16 상자다. 성공·일부 성공은 ZUS 토스트로 알린다(호출부 몫).
 * 아이콘과 문구를 함께 두어 색에만 기대지 않는다.
 */
import { Button } from '@zep-us/zus/button';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { BoltFillIcon, ExclamationmarkSmallCircleIcon, InformationmarkSmallCircleIcon } from '@zep-us/zus-icons';
import { PlanBadge } from '@/shared/components/Badge/PlanBadge';
import { cn } from '@/utils/tailwind.util';
import { type AiGenerateDockNoticeData, type AiGenerateDockNoticeTone } from '../types/aiGenerateDock.types';
const TONE_ICON = {
  neutral: <InformationmarkSmallCircleIcon size="sm" fill={ICON_COLOR.alternative} aria-hidden />,
  cautionary: <ExclamationmarkSmallCircleIcon size="sm" fill={ICON_COLOR.cautionary} aria-hidden />,
  destructive: <ExclamationmarkSmallCircleIcon size="sm" fill={ICON_COLOR.destructive} aria-hidden />,
  plan: <PlanBadge planType="BASIC" tooltip={false} />,
  login: null
} as const;

/** 업그레이드는 gradient, 로그인은 strong 채운 버튼, 나머지는 글자 버튼(Figma 5-5 · 5-7). */
const ACTION_INTENT = {
  neutral: 'neutral',
  cautionary: 'neutral',
  destructive: 'neutral',
  plan: 'gradient',
  login: 'strong'
} as const;

/**
 * 업그레이드(Basic) 버튼 색 — 가격 페이지 Basic 카드 CTA(BasicPricingCard)와 같은 mint→green 그라디언트로
 * ZUS gradient intent 의 기본 violet→blue 를 덮는다.
 */
const BASIC_UPGRADE_BUTTON_CLASS = 'border-transparent bg-gradient-to-r from-mint-600 to-green-500 hover:from-mint-700 hover:to-green-600';

/** 플랜·로그인 권유는 연보라 판 + 채운 버튼, 나머지는 흰 판 + 글자 버튼. */
const isPromotion = (tone: AiGenerateDockNoticeTone) => tone === 'plan' || tone === 'login';
export const AiGenerateDockNotice = ({
  notice
}: {
  notice: AiGenerateDockNoticeData;
}) => {
  const promotion = isPromotion(notice.tone);
  return <div data-testid="ai-generate-dock-notice" data-tone={notice.tone} role={notice.tone === 'destructive' ? 'alert' : 'status'} className={cn(
  // Figma notice-wrap(40544:325) — 모서리 16 · 좌 16 / 우 12 / 상하 12 · 간격 12 · shadow-md.
  'flex items-center gap-sm rounded-md py-sm pl-md pr-sm shadow-md',
  // Figma: 무료 소진(plan)은 green-50, 게스트(login)는 violet-50.
  notice.tone === 'plan' ? 'bg-green-50' : promotion ? 'bg-violet-50' : 'border border-opacity-black-3 bg-default')} data-sentry-component="AiGenerateDockNotice" data-sentry-source-file="AiGenerateDockNotice.tsx">
      <p className="flex min-w-0 flex-1 items-center gap-xs text-body-sm-semibold text-default">
        {/* 문구가 길어져도 아이콘·플랜 칩은 줄지 않는다(좁은 화면에서 칩이 세로로 길쭉해졌다). */}
        {TONE_ICON[notice.tone] && <span className="flex shrink-0">{TONE_ICON[notice.tone]}</span>}
        {notice.message}
      </p>
      {notice.action && <Button size="xs" variant={promotion ? 'contained' : 'transparent'} intent={ACTION_INTENT[notice.tone]} className={cn('shrink-0', notice.tone === 'plan' && BASIC_UPGRADE_BUTTON_CLASS)} data-testid="ai-generate-dock-notice-action" leftSlot={notice.tone === 'plan' ? <BoltFillIcon size="xs" fill={ICON_COLOR.inverse} aria-hidden /> : undefined} onClick={notice.action.onClick}>
          {notice.action.label}
        </Button>}
    </div>;
};